import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Stack,
  ButtonGroup,
  Typography,
} from "@mui/material";
import Selector from "components/images/tokens/Selector";
import PromptModal from "./PromptModal";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DownloadIcon from "@mui/icons-material/Download";
import ImageGallery from "../HorizontalGallery";
import { useDispatch, useSelector } from "react-redux";
import { selectQueryState } from "../../../store/firestore";
import { imageStudioActions } from "store/imageStudio";
import ModelSelectorMenu from "../ModelSelectorMenu";
import { useTheme } from "@emotion/react";
import { fonts } from "fonts";

const COLORS = [
  "Green",
  "Blue",
  "Red",
  "White",
  "Black",
  "Artifact",
  "Colorless",
];
const TYPES = [
  "Angel",
  "Bear",
  "Beast",
  "Bird",
  "Dragon",
  "Elephant",
  "Goblin",
  "Insect",
  "Knight",
  "Saproling",
  "Sliver",
  "Soldier",
  "Spirit",
  "Squirrel",
  "Thopter",
  "Wurm",
  "Zombie",
];

const ABILITIES = ["Flying", "Trample", "Shroud", ""];

const ART_TOP_LEFT = [174, 233];
const ART_BOT_RIGHT = [1325, 1301];

const STATS_X = 1287; // Bottom right x
const STATS_Y = 1920; // Bottom right y
const STATS_SIZE = 90;

const TYPE_X = 234; // Top left x
const TYPE_Y = 1393; // Top left y
const TYPE_SIZE = 70;

const SET_X = 1185; // Top left x
const SET_Y = 1375; // Top left y
const SET_SIZE = 80;

const ABILITY_X = 280; // Top left x
const ABILITY_Y = 1520; // Top left y
const ABILITY_SIZE = 70;

export default function TokenEditor() {
  const generatedImagesState = useSelector((state) =>
    selectQueryState(state, "generatedImages")
  );

  const dispatch = useDispatch();
  const tokenSettings = useSelector(
    (state) => state.imageStudio.mtgTokenSettings
  );

  const theme = useTheme();

  const [localImage, setLocalImage] = useState(null);

  const canvasRef = useRef();
  const fileInputRef = useRef();
  const [modalOpen, setModalOpen] = useState(false);

  const [setLogo, setSetLogo] = useState(null);

  const handleSelectLocalImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      //img.onload = () => setImage(img);
      img.onload = () => setLocalImage(img);
      dispatch(
        imageStudioActions.updateTokenSettings({ field: "image", value: img })
      );
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const loadImage = (src) =>
    new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = rej;
      img.src = src;
    });

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const drawText = ({
    ctx,
    text,
    x,
    y,
    color,
    size,
    shadow = false,
    isTopLeft = true,
  }) => {
    ctx.font = `${size}px MPlantin`;
    ctx.fillStyle = color;
    if (isTopLeft) {
      ctx.textBaseline = "top";
      ctx.textAlign = "left";
    } else {
      ctx.textAlign = "right";
      ctx.textBaseline = "bottom";
    }

    if (shadow) {
      ctx.shadowColor = "black";
      ctx.shadowOffsetX = 4;
      ctx.shadowOffsetY = 4;
      ctx.shadowBlur = 0;
    } else {
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.shadowBlur = 0;
    }
    ctx.fillText(text, x, y);
  };

  const drawStats = (ctx) => {
    drawText({
      ctx,
      text: `${tokenSettings.power}/${tokenSettings.toughness}`,
      x: STATS_X,
      y: STATS_Y,
      color: "white",
      size: STATS_SIZE,
      shadow: true,
      isTopLeft: false,
    });
  };

  const drawType = (ctx) => {
    const typeText = `${
      tokenSettings.color === "artifact" ? "Artifact " : ""
    }Creature - ${capitalize(tokenSettings.creatureType)}`;
    drawText({
      ctx,
      text: typeText,
      x: TYPE_X,
      y: TYPE_Y,
      color: "white",
      size: TYPE_SIZE,
      shadow: true,
      isTopLeft: true,
    });
  };

  function drawTextInBox(
    ctx,
    text,
    x1,
    y1,
    x2,
    y2,
    fontFamily = "Arial",
    maxFontSize = 100,
    color = "black"
  ) {
    ctx.fillStyle = color;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    const lines = text.split("\n");
    const boxWidth = x2 - x1;
    const boxHeight = y2 - y1;

    let fontSize = maxFontSize;

    // Binary search for the largest font size that fits
    let minSize = 1;
    let maxSize = maxFontSize;

    while (minSize < maxSize) {
      fontSize = Math.ceil((minSize + maxSize) / 2);
      ctx.font = `${fontSize}px ${fontFamily}`;

      // Measure the width of each line
      const maxLineWidth = Math.max(
        ...lines.map((line) => ctx.measureText(line).width)
      );
      const totalHeight = fontSize * lines.length; // Simple approximation, no line spacing

      if (maxLineWidth <= boxWidth && totalHeight <= boxHeight) {
        minSize = fontSize; // It fits, try bigger
      } else {
        maxSize = fontSize - 1; // Too big, reduce
      }
    }

    fontSize = minSize;
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const lineHeight = fontSize; // Approximate line height
    const startY =
      y1 + boxHeight / 2 - (lineHeight * lines.length) / 2 + lineHeight / 2;

    lines.forEach((line, index) => {
      const y = startY + index * lineHeight;
      const x = x1 + boxWidth / 2;
      ctx.fillText(line, x, y);
    });
  }

  const buildToken = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    try {
      const baseImg = await loadImage(
        `${
          process.env.PUBLIC_URL
        }/mtg/frames/${tokenSettings.color.toLocaleLowerCase()}.png`
      );
      const overlayImg = await loadImage(
        `${process.env.PUBLIC_URL}/mtg/other/overlay.png`
      );

      canvas.width = baseImg.width;
      canvas.height = baseImg.height;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(baseImg, 0, 0);

      // Draw creature art
      let [x1, y1] = ART_TOP_LEFT;
      let [x2, y2] = ART_BOT_RIGHT;
      if (localImage) {
        ctx.drawImage(localImage, x1, y1, x2 - x1, y2 - y1);
      }

      // Draw set logo
      if (setLogo) {
        ctx.drawImage(setLogo, SET_X, SET_Y, SET_SIZE, SET_SIZE);
      }

      drawStats(ctx);
      drawType(ctx);
      if (tokenSettings.ability) {
        drawText({
          ctx,
          text: capitalize(tokenSettings.ability),
          x: ABILITY_X,
          y: ABILITY_Y,
          color: "black",
          size: ABILITY_SIZE,
        });
      }

      // Main text
      let textTopLeft = [245, 1480];
      if (tokenSettings.ability) {
        textTopLeft = [245, 1580];
      }
      drawTextInBox(
        ctx,
        tokenSettings.text,
        textTopLeft[0],
        textTopLeft[1],
        1255,
        1790,
        "MPlantin",
        70
      );

      // Overlay
      ctx.drawImage(overlayImg, 0, 0);
    } catch (err) {
      console.error("Error building token:", err);
    }
  };

  const downloadImage = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = "token.png";
    link.click();
  };

  useEffect(() => {
    buildToken();
  }, [tokenSettings, localImage, setLogo]);

  const handleSelectGeneratedImage = (image) => {
    if (!image?.image?.imageUrl) return;
    console.log(image);
    dispatch(
      imageStudioActions.updateTokenSettings({
        field: "image",
        value: image.image.imageUrl,
      })
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      setSetLogo(img);
    };
    img.src = URL.createObjectURL(file);
  };

  useEffect(() => {
    if (tokenSettings.image) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = tokenSettings.image;
      console.log(img.src);
      img.onload = () => {
        setLocalImage(img);
      };
    }
  }, [tokenSettings.image]);

  return (
    <Stack sx={{ p: 2 }} spacing={1} alignItems="center">
      <PromptModal
        open={modalOpen}
        setOpen={setModalOpen}
        setImage={(image) =>
          dispatch(
            imageStudioActions.updateTokenSettings({
              field: "image",
              value: image,
            })
          )
        }
      />
      <TextField
        fullWidth
        select
        label="Color"
        value={tokenSettings.color}
        onChange={(e) =>
          dispatch(
            imageStudioActions.updateTokenSettings({
              field: "color",
              value: e.target.value,
            })
          )
        }
        sx={{ mr: 2, mb: 2 }}
      >
        {COLORS.map((c) => (
          <MenuItem key={c} value={c}>
            {c}
          </MenuItem>
        ))}
      </TextField>

      <Selector
        fullWidth
        value={tokenSettings.creatureType}
        setValue={(value) =>
          dispatch(
            imageStudioActions.updateTokenSettings({
              field: "creatureType",
              value,
            })
          )
        }
        options={TYPES}
        label={"Creature Type"}
      />

      <Button variant="outlined" component="label">
        Select Set Logo
        <input
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileChange}
        />
      </Button>

      <Stack direction="row" width="100%" spacing={1}>
        <TextField
          fullWidth
          label="Power"
          value={tokenSettings.power}
          onChange={(e) =>
            dispatch(
              imageStudioActions.updateTokenSettings({
                field: "power",
                value: e.target.value,
              })
            )
          }
        />
        <TextField
          fullWidth
          label="Toughness"
          value={tokenSettings.toughness}
          onChange={(e) =>
            dispatch(
              imageStudioActions.updateTokenSettings({
                field: "toughness",
                value: e.target.value,
              })
            )
          }
        />
      </Stack>

      <Selector
        fullWidth
        value={tokenSettings.ability}
        setValue={(value) =>
          dispatch(
            imageStudioActions.updateTokenSettings({ field: "ability", value })
          )
        }
        options={ABILITIES}
        label={"Ability"}
      />

      <TextField
        label="Text"
        value={tokenSettings.text}
        onChange={(e) =>
          dispatch(
            imageStudioActions.updateTokenSettings({
              field: "text",
              value: e.target.value,
            })
          )
        }
        multiline
        rows={2}
        fullWidth
      />


      <ButtonGroup fullWidth>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setModalOpen(true)}
          endIcon={<AutoAwesomeIcon />}
        >
          Generate
        </Button>
        <Box width="100%">
          <Button
            variant="contained"
            fullWidth
            onClick={() => fileInputRef.current.click()}
            endIcon={<AddPhotoAlternateIcon />}
          >
            Select
          </Button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleSelectLocalImage}
          />
        </Box>
      </ButtonGroup>
      <ImageGallery
        loading={generatedImagesState.loading}
        images={generatedImagesState.docs}
        onClick={handleSelectGeneratedImage}
        size={150}
        showMenu={false}
      />
      {tokenSettings.color && (
        <canvas
          ref={canvasRef}
          style={{
            border: "3px solid {white}",
            borderRadius: "4px",
            maxWidth: "300px",
          }}
        />
      )}
      <Button
        variant="contained"
        onClick={downloadImage}
        disabled={!tokenSettings.image}
        endIcon={<DownloadIcon />}
      >
        Download
      </Button>
    </Stack>
  );
}
